import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CreditCard,
  Save,
  Eye,
  EyeOff,
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export default function PaymentSettingsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [testingPayPal, setTestingPayPal] = useState(false);
  const [paypalTestResult, setPaypalTestResult] = useState<any>(null);
  const [showPaypalDetails, setShowPaypalDetails] = useState(false);

  useEffect(() => {
    if (profile) {
      if (profile.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      fetchSettings();
    }
  }, [profile, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .in('setting_key', [
        'paypal_client_id',
        'paypal_client_secret',
        'paypal_mode',
        'stripe_publishable_key',
        'stripe_secret_key',
        'stripe_mode',
        'bank_transfer_enabled',
        'bank_name',
        'bank_iban',
        'bank_swift',
        'bank_account_holder'
      ]);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le impostazioni',
        variant: 'destructive',
      });
    } else if (data) {
      const settingsMap: Record<string, string> = {};
      data.forEach((setting: any) => {
        settingsMap[setting.setting_key] = setting.setting_value || '';
      });
      setSettings(settingsMap);
    }
    setLoading(false);
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);

    try {
      const updates = Object.entries(settings).map(([key, value]) =>
        supabase
          .from('app_settings')
          .update({
            setting_value: value,
            updated_at: new Date().toISOString(),
            updated_by: profile?.id,
          })
          .eq('setting_key', key)
      );

      const results = await Promise.all(updates);
      const hasError = results.some(result => result.error);

      if (hasError) {
        throw new Error('Failed to update some settings');
      }

      toast({
        title: 'Impostazioni salvate',
        description: 'Le configurazioni di pagamento sono state aggiornate',
      });

      setPaypalTestResult(null);
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile salvare le impostazioni',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestPayPal = async () => {
    setTestingPayPal(true);
    setPaypalTestResult(null);

    try {
      const response = await fetch('/api/test/paypal', {
        method: 'POST',
      });

      const data = await response.json();

      setPaypalTestResult({
        success: data.success,
        message: data.message || data.error,
        details: data.details,
      });

      if (data.success) {
        toast({
          title: 'Test PayPal riuscito',
          description: data.message,
        });
      } else {
        toast({
          title: 'Test PayPal fallito',
          description: data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      setPaypalTestResult({
        success: false,
        message: 'Errore durante il test della connessione',
      });
      toast({
        title: 'Errore',
        description: 'Impossibile testare la connessione PayPal',
        variant: 'destructive',
      });
    } finally {
      setTestingPayPal(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard/settings')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CreditCard className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Configurazione Pagamenti</h1>
              <p className="text-gray-600 mt-1">Gestisci le integrazioni di pagamento</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="paypal" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="paypal">PayPal</TabsTrigger>
            <TabsTrigger value="stripe">Stripe</TabsTrigger>
            <TabsTrigger value="bank">Bonifico</TabsTrigger>
          </TabsList>

          <TabsContent value="paypal" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>PayPal Configuration</CardTitle>
                <CardDescription>
                  Configura le credenziali API di PayPal per accettare pagamenti
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="paypal_client_id">Client ID</Label>
                  <div className="relative">
                    <Input
                      id="paypal_client_id"
                      type={showSecrets['paypal_client_id'] ? 'text' : 'password'}
                      value={settings['paypal_client_id'] || ''}
                      onChange={(e) => handleSettingChange('paypal_client_id', e.target.value)}
                      placeholder="Inserisci il Client ID di PayPal"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowSecret('paypal_client_id')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showSecrets['paypal_client_id'] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paypal_client_secret">Client Secret</Label>
                  <div className="relative">
                    <Input
                      id="paypal_client_secret"
                      type={showSecrets['paypal_client_secret'] ? 'text' : 'password'}
                      value={settings['paypal_client_secret'] || ''}
                      onChange={(e) => handleSettingChange('paypal_client_secret', e.target.value)}
                      placeholder="Inserisci il Client Secret di PayPal"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowSecret('paypal_client_secret')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showSecrets['paypal_client_secret'] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paypal_mode">Modalita</Label>
                  <select
                    id="paypal_mode"
                    value={settings['paypal_mode'] || 'sandbox'}
                    onChange={(e) => handleSettingChange('paypal_mode', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="sandbox">Sandbox (Test)</option>
                    <option value="live">Live (Produzione)</option>
                  </select>
                </div>

                <div className="pt-2">
                  <p className="text-xs text-gray-500">
                    Ottieni le tue chiavi dal{' '}
                    <a
                      href="https://developer.paypal.com/dashboard/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Dashboard PayPal Developer
                    </a>
                  </p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="text-sm font-medium">Test Connessione</h4>
                  <Button
                    type="button"
                    onClick={handleTestPayPal}
                    disabled={testingPayPal || !settings['paypal_client_id'] || !settings['paypal_client_secret']}
                    variant="outline"
                    className="w-full"
                  >
                    {testingPayPal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Testa PayPal
                  </Button>

                  {paypalTestResult && (
                    <div className={`p-3 rounded-lg ${
                      paypalTestResult.success
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        {paypalTestResult.success ? (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            paypalTestResult.success ? 'text-green-900' : 'text-red-900'
                          }`}>
                            {paypalTestResult.success ? 'Connessione riuscita' : 'Connessione fallita'}
                          </p>
                          <p className={`text-xs mt-1 ${
                            paypalTestResult.success ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {paypalTestResult.message}
                          </p>
                        </div>
                      </div>

                      {paypalTestResult.details && !paypalTestResult.success && (
                        <Collapsible open={showPaypalDetails} onOpenChange={setShowPaypalDetails} className="mt-3">
                          <CollapsibleTrigger className="flex items-center gap-2 text-xs text-red-700 hover:text-red-800 font-medium">
                            <AlertCircle className="w-3 h-3" />
                            {showPaypalDetails ? 'Nascondi dettagli' : 'Mostra dettagli'}
                            {showPaypalDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-2">
                            <div className="bg-red-100 p-2 rounded text-xs font-mono text-red-900 overflow-auto max-h-40">
                              <pre className="whitespace-pre-wrap break-words">
                                {JSON.stringify(paypalTestResult.details, null, 2)}
                              </pre>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="stripe" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Stripe Configuration</CardTitle>
                <CardDescription>
                  Configura le credenziali API di Stripe per accettare pagamenti
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="stripe_publishable_key">Publishable Key</Label>
                  <Input
                    id="stripe_publishable_key"
                    type="text"
                    value={settings['stripe_publishable_key'] || ''}
                    onChange={(e) => handleSettingChange('stripe_publishable_key', e.target.value)}
                    placeholder="pk_test_..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripe_secret_key">Secret Key</Label>
                  <div className="relative">
                    <Input
                      id="stripe_secret_key"
                      type={showSecrets['stripe_secret_key'] ? 'text' : 'password'}
                      value={settings['stripe_secret_key'] || ''}
                      onChange={(e) => handleSettingChange('stripe_secret_key', e.target.value)}
                      placeholder="sk_test_..."
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowSecret('stripe_secret_key')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showSecrets['stripe_secret_key'] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripe_mode">Modalita</Label>
                  <select
                    id="stripe_mode"
                    value={settings['stripe_mode'] || 'test'}
                    onChange={(e) => handleSettingChange('stripe_mode', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="test">Test</option>
                    <option value="live">Live (Produzione)</option>
                  </select>
                </div>

                <div className="pt-2">
                  <p className="text-xs text-gray-500">
                    Ottieni le tue chiavi dal{' '}
                    <a
                      href="https://dashboard.stripe.com/apikeys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Dashboard Stripe
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bank" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Bonifico Bancario</CardTitle>
                <CardDescription>
                  Configura i dati bancari per accettare pagamenti tramite bonifico
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Abilita bonifico bancario</Label>
                    <p className="text-sm text-gray-500">
                      Consenti agli utenti di pagare tramite bonifico
                    </p>
                  </div>
                  <Switch
                    checked={settings['bank_transfer_enabled'] === 'true'}
                    onCheckedChange={(checked) =>
                      handleSettingChange('bank_transfer_enabled', checked ? 'true' : 'false')
                    }
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="bank_name">Nome Banca</Label>
                  <Input
                    id="bank_name"
                    type="text"
                    value={settings['bank_name'] || ''}
                    onChange={(e) => handleSettingChange('bank_name', e.target.value)}
                    placeholder="Es. Banca Intesa"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_account_holder">Intestatario</Label>
                  <Input
                    id="bank_account_holder"
                    type="text"
                    value={settings['bank_account_holder'] || ''}
                    onChange={(e) => handleSettingChange('bank_account_holder', e.target.value)}
                    placeholder="Nome e cognome o ragione sociale"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_iban">IBAN</Label>
                  <Input
                    id="bank_iban"
                    type="text"
                    value={settings['bank_iban'] || ''}
                    onChange={(e) => handleSettingChange('bank_iban', e.target.value)}
                    placeholder="IT00X0000000000000000000000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_swift">SWIFT/BIC</Label>
                  <Input
                    id="bank_swift"
                    type="text"
                    value={settings['bank_swift'] || ''}
                    onChange={(e) => handleSettingChange('bank_swift', e.target.value)}
                    placeholder="BCITITMM"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Nota:</strong> I pagamenti tramite bonifico richiederanno una verifica manuale prima dell'attivazione del corso.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            size="lg"
            className="min-w-[200px]"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
